import React, { useEffect, useRef } from 'react'
import { formatSSN, validateSSN } from '@/shared/utils'
import type { IdentityInfoProps } from '@/features/enrollment'

export default function IdentityInfo({ enrollmentData, updateEnrollmentData, errors }: IdentityInfoProps) {
  // ✅ Check if this is a Roblox campaign
  const isRobloxCampaign = enrollmentData.campaignId === 'roblox-exploitation' || 
                           enrollmentData.selectedCampaign === 'robloxExploitation' ||
                           enrollmentData.campaign?.name?.toLowerCase().includes('roblox')
  
  // Track if we've already populated DOB to avoid infinite loops
  const hasPopulatedDOB = useRef(false)
  
  // ✅ Auto-populate injuredPartyDOB from intakeDOB (Step 1 → Step 2)
  // This runs whenever intakeDOB changes or on mount to ensure Step 2 always reflects Step 1
  useEffect(() => {
    // Only run for Roblox campaigns
    if (!isRobloxCampaign) return
    
    // ✅ Get intakeDOB from multiple possible locations (flat or nested step data)
    const intakeDOB = enrollmentData.intakeDOB || 
                      (enrollmentData as any).step1?.intakeDOB ||
                      (enrollmentData as any).step0?.intakeDOB
    
    // If intakeDOB exists, always populate from Step 1 (Step 1 takes precedence)
    if (intakeDOB) {
      // Always update injuredPartyDOB to match intakeDOB
      if (enrollmentData.injuredPartyDOB !== intakeDOB) {
        updateEnrollmentData('injuredPartyDOB', intakeDOB)
      }
      
      // ✅ Auto-populate birthMonth, birthDay, birthYear from intakeDOB
      try {
        const date = new Date(intakeDOB)
        if (!isNaN(date.getTime())) {
          const month = date.getMonth() + 1 // getMonth() returns 0-11
          const day = date.getDate()
          const year = date.getFullYear()
          
          // ✅ Always sync from Step 1's intakeDOB (overwrites any existing values)
          // This ensures Step 2 always reflects Step 1's date, even if old data exists
          // Check if values need updating to avoid unnecessary re-renders
          const needsUpdate = 
            !enrollmentData.birthMonth || 
            !enrollmentData.birthDay || 
            !enrollmentData.birthYear ||
            enrollmentData.birthMonth?.toString() !== month.toString() ||
            enrollmentData.birthDay?.toString() !== day.toString() ||
            enrollmentData.birthYear?.toString() !== year.toString()
          
          if (needsUpdate) {
            updateEnrollmentData('birthMonth', month.toString())
            updateEnrollmentData('birthDay', day.toString())
            updateEnrollmentData('birthYear', year.toString())
            hasPopulatedDOB.current = true
          }
        }
      } catch (error) {
        console.error('Error parsing intakeDOB for month/day/year:', error)
      }
    } else {
      // Reset flag if intakeDOB is cleared
      hasPopulatedDOB.current = false
    }
  }, [enrollmentData.intakeDOB, enrollmentData.injuredPartyDOB, enrollmentData.birthMonth, enrollmentData.birthDay, enrollmentData.birthYear, isRobloxCampaign, updateEnrollmentData]) // ✅ Include birth fields to detect when they need updating

  return (
    <>
      {/* Injured Party DOB - Show month/day/year dropdowns for Roblox campaigns */}
      {isRobloxCampaign ? (
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Date of Birth of the Gamer <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div>
              <label className="block text-xs text-accent-600 mb-1">Month</label>
              <select
                name="birthMonth"
                className={`input-field w-full text-xs sm:text-sm ${errors.birthMonth ? 'border-red-500' : ''}`}
                value={enrollmentData.birthMonth || ''}
                onChange={(e) => updateEnrollmentData('birthMonth', e.target.value)}
              >
                <option value="">Select month</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.birthMonth && (
                <p className="text-xs text-red-600 mt-1">{errors.birthMonth}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-accent-600 mb-1">Day</label>
              <select
                name="birthDay"
                className={`input-field w-full text-xs sm:text-sm ${errors.birthDay ? 'border-red-500' : ''}`}
                value={enrollmentData.birthDay || ''}
                onChange={(e) => updateEnrollmentData('birthDay', e.target.value)}
              >
                <option value="">Select day</option>
                {Array.from({ length: 31 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.birthDay && (
                <p className="text-xs text-red-600 mt-1">{errors.birthDay}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-accent-600 mb-1">Year</label>
              <select
                name="birthYear"
                className={`input-field w-full text-xs sm:text-sm ${errors.birthYear ? 'border-red-500' : ''}`}
                value={enrollmentData.birthYear || ''}
                onChange={(e) => updateEnrollmentData('birthYear', e.target.value)}
              >
                <option value="">Select Year</option>
                {Array.from({ length: 80 }, (_, i) => {
                  const year = new Date().getFullYear() - i
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  )
                })}
              </select>
              {errors.birthYear && (
                <p className="text-xs text-red-600 mt-1">{errors.birthYear}</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Injured Party DOB <span className="text-red-500">*</span>
          </label>
          <input 
            type="date" 
            className={`input-field ${errors.injuredPartyDOB ? 'border-red-500' : ''}`} 
            value={enrollmentData.injuredPartyDOB} 
            onChange={(e) => updateEnrollmentData('injuredPartyDOB', e.target.value)} 
          />
          {errors.injuredPartyDOB && (
            <p className="mt-1 text-sm text-red-600">{errors.injuredPartyDOB}</p>
          )}
        </div>
      )}

      {/* SSN */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          Injured Party SSN <span className="text-red-500">*</span>
        </label>
        <input 
          type="text" 
          className={`input-field ${errors.ssn ? 'border-red-500' : ''}`} 
          placeholder="Enter SSN (full or last 4 digits)" 
          value={enrollmentData.ssn} 
          onChange={(e) => {
            const rawValue = e.target.value;
            const formattedValue = formatSSN(rawValue);
            updateEnrollmentData('ssn', formattedValue);
          }}
          onBlur={(e) => {
            const validation = validateSSN(e.target.value);
            if (!validation.isValid && validation.error) {
              // Note: This would need setErrors to be passed as a prop or handled differently
              // For now, we'll just update the enrollment data
            }
          }}
        />
        {errors.ssn && (
          <p className="mt-1 text-sm text-red-600">{errors.ssn}</p>
        )}
        <p className="text-xs text-accent-500 mt-1">
          Enter 4 digits for last 4 SSN (xxx-xx-1234) or 9 digits for full SSN (123-12-1234)
        </p>
      </div>
    </>
  )
}
